(() => {
  const FILTER_PARAMS = {
    year: 'filter.v.m.custom.ymme_year',
    make: 'filter.v.m.custom.ymme_make',
    model: 'filter.v.m.custom.ymme_model',
    engine: 'filter.v.m.custom.ymme_engine'
  };
  const LEGACY_STORAGE_KEY = 'ymme_garage';
  const GARAGE_STORAGE_KEY = 'flashark_ymme_garage_v2';
  const MAX_VEHICLES = 5;
  const FIELD_ORDER = ['year', 'make', 'model', 'engine'];
  const treeRequests = new Map();

  const decodeText = (value) => {
    const text = String(value || '');
    if (!text.includes('&')) return text;
    return new DOMParser().parseFromString(text, 'text/html').documentElement.textContent;
  };

  const buildTree = (list) => (Array.isArray(list) ? list : []).reduce((tree, item) => {
    const [year, make, model, engine] = decodeText(item).trim().split('|');
    if (!year || !make || !model || !engine) return tree;
    tree[year] ||= {};
    tree[year][make] ||= {};
    tree[year][make][model] ||= [];
    if (!tree[year][make][model].includes(engine)) tree[year][make][model].push(engine);
    return tree;
  }, {});

  const loadTree = (url) => {
    if (!treeRequests.has(url)) {
      treeRequests.set(url, fetch(url, { credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error(`YMME data request failed: ${response.status}`);
        return response.json();
      }).then(buildTree));
    }
    return treeRequests.get(url);
  };

  const vehicleId = (vehicle) => FIELD_ORDER.map((field) => vehicle?.[field] || '').join('|');
  const vehicleName = (vehicle) => FIELD_ORDER.map((field) => vehicle?.[field] || '').filter(Boolean).join(' ');
  const validVehicle = (vehicle) => FIELD_ORDER.every((field) => String(vehicle?.[field] || '').trim() !== '');

  const emptyGarage = () => ({ version: 2, activeVehicleId: null, vehicles: [] });

  const readGarage = () => {
    let garage = emptyGarage();
    try {
      const stored = JSON.parse(localStorage.getItem(GARAGE_STORAGE_KEY));
      if (stored && Array.isArray(stored.vehicles)) {
        garage.vehicles = stored.vehicles.filter(validVehicle).slice(0, MAX_VEHICLES);
        garage.activeVehicleId = stored.activeVehicleId || null;
      }
    } catch (_) {}

    if (garage.vehicles.length === 0) {
      try {
        const legacyVehicle = JSON.parse(localStorage.getItem(LEGACY_STORAGE_KEY));
        if (validVehicle(legacyVehicle)) {
          garage.vehicles = [legacyVehicle];
          garage.activeVehicleId = vehicleId(legacyVehicle);
        }
      } catch (_) {}
    }

    const unique = new Map();
    garage.vehicles.forEach((vehicle) => unique.set(vehicleId(vehicle), vehicle));
    garage.vehicles = [...unique.values()].slice(0, MAX_VEHICLES);
    if (!garage.vehicles.some((vehicle) => vehicleId(vehicle) === garage.activeVehicleId)) garage.activeVehicleId = null;
    return garage;
  };

  const writeGarage = (garage) => {
    const normalized = {
      version: 2,
      activeVehicleId: garage.activeVehicleId || null,
      vehicles: garage.vehicles.filter(validVehicle).slice(0, MAX_VEHICLES)
    };
    try {
      localStorage.setItem(GARAGE_STORAGE_KEY, JSON.stringify(normalized));
      const active = normalized.vehicles.find((vehicle) => vehicleId(vehicle) === normalized.activeVehicleId);
      if (active) localStorage.setItem(LEGACY_STORAGE_KEY, JSON.stringify(active));
      else localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch (_) {}
    return normalized;
  };

  const upsertVehicle = (garage, vehicle, editingId = null) => {
    const id = vehicleId(vehicle);
    const vehicles = garage.vehicles.filter((item) => {
      const itemId = vehicleId(item);
      return itemId !== id && itemId !== editingId;
    });
    vehicles.unshift(vehicle);
    return writeGarage({ version: 2, activeVehicleId: id, vehicles: vehicles.slice(0, MAX_VEHICLES) });
  };

  const updateCart = (vehicle) => {
    const rootUrl = window.Shopify?.routes?.root || '/';
    const attributes = {
      YMME_Year: vehicle?.year || '',
      YMME_Make: vehicle?.make || '',
      YMME_Model: vehicle?.model || '',
      YMME_Engine: vehicle?.engine || '',
      YMME_Vehicle: vehicle ? vehicleName(vehicle) : ''
    };
    return fetch(`${rootUrl}cart/update.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes })
    });
  };

  const redirectToVehicle = (root, vehicle) => {
    const url = new URL(root.dataset.searchUrl || '/collections/all', window.location.origin);
    Object.entries(FILTER_PARAMS).forEach(([field, parameter]) => url.searchParams.set(parameter, vehicle[field]));
    window.location.assign(url.toString());
  };

  const option = (value, label = value) => new Option(label, value);

  const createFieldsController = (root, fieldSelector, actionButton) => {
    const fields = Object.fromEntries([...root.querySelectorAll(fieldSelector)].map((select) => {
      const key = select.dataset.ymmeField || select.dataset.mobileYmmeField;
      return [key, select];
    }));
    const placeholders = Object.fromEntries(FIELD_ORDER.map((field) => [field, fields[field]?.options[0]?.textContent || field]));
    let tree = {};
    let loaded = false;

    const setActionState = () => {
      if (actionButton) actionButton.disabled = !FIELD_ORDER.every((field) => fields[field]?.value);
    };

    const fill = (field, values) => {
      const select = fields[field];
      if (!select) return;
      select.replaceChildren(option('', placeholders[field]));
      values.forEach((value) => select.add(option(value)));
      select.disabled = values.length === 0;
    };

    const refresh = (field) => {
      if (field === 'year') {
        fill('make', Object.keys(tree[fields.year.value] || {}).sort());
        fill('model', []);
        fill('engine', []);
      } else if (field === 'make') {
        fill('model', Object.keys(tree[fields.year.value]?.[fields.make.value] || {}).sort());
        fill('engine', []);
      } else if (field === 'model') {
        fill('engine', [...(tree[fields.year.value]?.[fields.make.value]?.[fields.model.value] || [])].sort());
      }
      setActionState();
    };

    Object.entries(fields).forEach(([field, select]) => select.addEventListener('change', () => refresh(field)));

    return {
      async load(url) {
        if (loaded) return;
        tree = await loadTree(url);
        fill('year', Object.keys(tree).sort().reverse());
        loaded = true;
      },
      getVehicle() {
        return Object.fromEntries(FIELD_ORDER.map((field) => [field, fields[field]?.value || '']));
      },
      setVehicle(vehicle) {
        if (!loaded || !validVehicle(vehicle) || !tree[vehicle.year]) return false;
        fields.year.value = vehicle.year;
        refresh('year');
        fields.make.value = vehicle.make;
        refresh('make');
        fields.model.value = vehicle.model;
        refresh('model');
        fields.engine.value = vehicle.engine;
        setActionState();
        return true;
      },
      reset() {
        if (fields.year) fields.year.value = '';
        fill('make', []);
        fill('model', []);
        fill('engine', []);
        setActionState();
      }
    };
  };

  const initDesktopYmme = async (root) => {
    const searchButton = root.querySelector('[data-ymme-search]');
    const resetButton = root.querySelector('[data-ymme-reset]');
    if (!searchButton || !resetButton) return;
    const controller = createFieldsController(root, '[data-ymme-field]', searchButton);

    try {
      await controller.load(root.dataset.jsonUrl);
      const garage = readGarage();
      const active = garage.vehicles.find((vehicle) => vehicleId(vehicle) === garage.activeVehicleId);
      if (active) controller.setVehicle(active);
    } catch (error) {
      console.error('Flashark header YMME:', error);
    }

    searchButton.addEventListener('click', async () => {
      const vehicle = controller.getVehicle();
      if (!validVehicle(vehicle)) return;
      upsertVehicle(readGarage(), vehicle);
      try { await updateCart(vehicle); } catch (_) {}
      redirectToVehicle(root, vehicle);
    });

    resetButton.addEventListener('click', async () => {
      const garage = readGarage();
      garage.activeVehicleId = null;
      writeGarage(garage);
      controller.reset();
      try { await updateCart(null); } catch (_) {}
    });
  };

  const createVehicleRow = (vehicle, active = false) => {
    const row = document.createElement('div');
    row.className = active ? 'flashark-vehicle-garage__active' : 'flashark-vehicle-garage__vehicle';

    const name = document.createElement(active ? 'span' : 'button');
    name.className = 'flashark-vehicle-garage__vehicle-name';
    const icon = document.createElement('img');
    icon.src = 'https://cdn.shopify.com/s/files/1/0293/4423/5619/files/Slice_1.png?v=1787293359';
    icon.width = 16;
    icon.height = 16;
    icon.alt = '';
    name.append(icon, document.createTextNode(vehicleName(vehicle)));
    if (!active) {
      name.type = 'button';
      name.dataset.vehicleAction = 'select';
      name.dataset.vehicleId = vehicleId(vehicle);
    }
    row.appendChild(name);

    const edit = document.createElement('button');
    edit.type = 'button';
    edit.className = 'flashark-vehicle-garage__icon-button';
    edit.dataset.vehicleAction = 'edit';
    edit.dataset.vehicleId = vehicleId(vehicle);
    edit.setAttribute('aria-label', `Edit ${vehicleName(vehicle)}`);
    edit.textContent = '✎';
    row.appendChild(edit);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'flashark-vehicle-garage__icon-button';
    remove.dataset.vehicleAction = 'delete';
    remove.dataset.vehicleId = vehicleId(vehicle);
    remove.setAttribute('aria-label', `Delete ${vehicleName(vehicle)}`);
    remove.textContent = '×';
    row.appendChild(remove);
    return row;
  };

  const initMobileYmme = (modal) => {
    const formView = modal.querySelector('[data-flashark-vehicle-form]');
    const garageView = modal.querySelector('[data-flashark-vehicle-garage]');
    const saveButton = modal.querySelector('[data-flashark-vehicle-save]');
    const message = modal.querySelector('[data-flashark-vehicle-message]');
    const activeContainer = modal.querySelector('[data-flashark-active-vehicle]');
    const activeActions = modal.querySelector('[data-flashark-active-actions]');
    const otherContainer = modal.querySelector('[data-flashark-other-vehicles]');
    const emptyMessage = modal.querySelector('[data-flashark-vehicle-empty]');
    const addButton = modal.querySelector('[data-flashark-add-vehicle]');
    const browseButton = modal.querySelector('[data-flashark-browse-parts]');
    const shopWithoutButton = modal.querySelector('[data-flashark-shop-without]');
    const controller = createFieldsController(modal, '[data-mobile-ymme-field]', saveButton);
    let editingId = null;
    let loaded = false;
    let lastFocused = null;

    const findVehicle = (garage, id) => garage.vehicles.find((vehicle) => vehicleId(vehicle) === id);

    const showForm = async (vehicle = null) => {
      formView.hidden = false;
      garageView.hidden = true;
      message.textContent = '';
      if (!loaded) {
        try {
          await controller.load(modal.dataset.jsonUrl);
          loaded = true;
        } catch (error) {
          message.textContent = 'Vehicle data could not be loaded. Please try again.';
          console.error('Flashark mobile YMME:', error);
          return;
        }
      }
      controller.reset();
      editingId = vehicle ? vehicleId(vehicle) : null;
      if (vehicle) controller.setVehicle(vehicle);
    };

    const renderGarage = () => {
      const garage = readGarage();
      const active = findVehicle(garage, garage.activeVehicleId);
      activeContainer.replaceChildren();
      otherContainer.replaceChildren();

      if (active) activeContainer.appendChild(createVehicleRow(active, true));
      activeActions.hidden = !active;

      garage.vehicles.filter((vehicle) => vehicleId(vehicle) !== garage.activeVehicleId).forEach((vehicle) => {
        otherContainer.appendChild(createVehicleRow(vehicle));
      });

      emptyMessage.hidden = garage.vehicles.length !== 0;
      addButton.disabled = garage.vehicles.length >= MAX_VEHICLES;
      addButton.title = addButton.disabled ? `You can save up to ${MAX_VEHICLES} vehicles.` : '';
      formView.hidden = true;
      garageView.hidden = false;
    };

    const openModal = async (trigger) => {
      lastFocused = trigger || document.activeElement;
      trigger?.setAttribute('aria-expanded', 'true');
      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.documentElement.classList.add('flashark-vehicle-modal-lock');
      const garage = readGarage();
      if (garage.vehicles.length) renderGarage();
      else await showForm();
      window.requestAnimationFrame(() => modal.querySelector('button:not([disabled]),select:not([disabled])')?.focus());
    };

    const closeModal = () => {
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.documentElement.classList.remove('flashark-vehicle-modal-lock');
      lastFocused?.setAttribute?.('aria-expanded', 'false');
      lastFocused?.focus?.();
    };

    document.querySelectorAll(`[aria-controls="${modal.id}"][data-flashark-vehicle-open]`).forEach((button) => {
      button.addEventListener('click', () => openModal(button));
    });
    modal.querySelectorAll('[data-flashark-vehicle-close]').forEach((button) => button.addEventListener('click', closeModal));

    saveButton.addEventListener('click', async () => {
      const vehicle = controller.getVehicle();
      if (!validVehicle(vehicle)) return;
      const garage = readGarage();
      const isExisting = garage.vehicles.some((item) => vehicleId(item) === vehicleId(vehicle));
      if (!editingId && !isExisting && garage.vehicles.length >= MAX_VEHICLES) {
        message.textContent = `You can save up to ${MAX_VEHICLES} vehicles.`;
        return;
      }
      upsertVehicle(garage, vehicle, editingId);
      try { await updateCart(vehicle); } catch (_) {}
      closeModal();
      redirectToVehicle(modal, vehicle);
    });

    addButton.addEventListener('click', () => {
      if (!addButton.disabled) showForm();
    });

    browseButton.addEventListener('click', async () => {
      const garage = readGarage();
      const active = findVehicle(garage, garage.activeVehicleId);
      if (!active) return;
      try { await updateCart(active); } catch (_) {}
      redirectToVehicle(modal, active);
    });

    shopWithoutButton.addEventListener('click', async () => {
      const garage = readGarage();
      garage.activeVehicleId = null;
      writeGarage(garage);
      try { await updateCart(null); } catch (_) {}
      renderGarage();
    });

    modal.addEventListener('click', async (event) => {
      const action = event.target.closest('[data-vehicle-action]');
      if (!action) return;
      const garage = readGarage();
      const vehicle = findVehicle(garage, action.dataset.vehicleId);
      if (!vehicle) return;

      if (action.dataset.vehicleAction === 'select') {
        garage.activeVehicleId = vehicleId(vehicle);
        writeGarage(garage);
        try { await updateCart(vehicle); } catch (_) {}
        renderGarage();
      } else if (action.dataset.vehicleAction === 'edit') {
        await showForm(vehicle);
      } else if (action.dataset.vehicleAction === 'delete') {
        garage.vehicles = garage.vehicles.filter((item) => vehicleId(item) !== vehicleId(vehicle));
        if (garage.activeVehicleId === vehicleId(vehicle)) garage.activeVehicleId = garage.vehicles[0] ? vehicleId(garage.vehicles[0]) : null;
        const normalized = writeGarage(garage);
        const newActive = findVehicle(normalized, normalized.activeVehicleId);
        try { await updateCart(newActive || null); } catch (_) {}
        if (normalized.vehicles.length) renderGarage();
        else showForm();
      }
    });

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') closeModal();
      if (event.key !== 'Tab') return;
      const focusable = [...modal.querySelectorAll('button:not([disabled]),select:not([disabled]),a[href],input:not([disabled])')].filter((item) => item.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-header-ymme]').forEach(initDesktopYmme);
    document.querySelectorAll('[data-mobile-ymme]').forEach(initMobileYmme);
  });
})();
